import {
    onMounted,
    ref,
    Ref,
} from '@vue/composition-api';

export function useSwipe(
    element: Ref<HTMLElement | undefined>,
    options?: SwipeOptions,
) {
    const {
        minSwipeDistance = 20,
        supportMouseEvents = false,
        contain = false,
    } = options || {};

    const touchStartX = ref<number>();
    const touchStartY = ref<number>();

    const handlers: Record<Direction, (() => void) | null> = {
        up: null,
        down: null,
        left: null,
        right: null,
    };

    onMounted(() => {
        if (!element.value) {
            return;
        }

        element.value.addEventListener('touchstart', onTouchStart, { passive: true });
        element.value.addEventListener('touchend', onTouchEnd, { passive: true });
        if (contain) {
            element.value.addEventListener('touchmove', (event) => {
                event.preventDefault();
            });
        }

        if (supportMouseEvents) {
            element.value.addEventListener('mousedown', onClickStart, { passive: true });
            element.value.addEventListener('mouseup', onClickEnd, { passive: true });

            if (contain) {
                element.value.addEventListener('mousemove', (event) => {
                    event.preventDefault();
                });
            }
        }
    });

    function onTouchStart(event: TouchEvent) {
        if (!event.touches.length) {
            return;
        }

        const {
            screenX,
            screenY,
        } = event.touches[0]!;

        onMoveStart(screenX, screenY);
    }

    function onClickStart(event: MouseEvent) {
        onMoveStart(event.screenX, event.screenY);
    }

    function onMoveStart(x: number, y: number): void {
        touchStartX.value = x;
        touchStartY.value = y;
    }

    function onTouchEnd(event: TouchEvent) {
        const {
            screenX,
            screenY,
        } = event.changedTouches[0]!;

        onMoveEnd(screenX, screenY);
    }

    function onClickEnd(event: MouseEvent) {
        onMoveEnd(event.screenX, event.screenY);
    }

    function onMoveEnd(x: number, y: number): void {
        const diffX = x - touchStartX.value!;
        const diffY = y - touchStartY.value!;

        if (Math.abs(diffX) < minSwipeDistance && Math.abs(diffY) < minSwipeDistance) {
            return;
        }

        if (Math.abs(diffX) >= Math.abs(diffY)) {
            if (diffX > 0) {
                handlers.left?.();
            } else {
                handlers.right?.();
            }
        } else {
            if (diffY > 0) {
                handlers.up?.();
            } else {
                handlers.down?.();
            }
        }
    }

    return {
        onRightSwipe: (handler: () => void) => {
            handlers.right = handler;
        },
        onLeftSwipe: (handler: () => void) => {
            handlers.left = handler;
        },
        onUpSwipe: (handler: () => void) => {
            handlers.up = handler;
        },
        onDownSwipe: (handler: () => void) => {
            handlers.down = handler;
        },
    };
}

type SwipeOptions = {
    minSwipeDistance?: number;
    supportMouseEvents?: boolean;
    contain?: boolean;
};

type Direction = 'up' | 'down' | 'left' | 'right';
